import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress,
    Alert,
    Tooltip
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Add as AddIcon
} from '@mui/icons-material';
import { adminInstance } from '../../utils/axios';

const AdminManagement = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedAdmin, setSelectedAdmin] = useState(null);
    const [newAdminData, setNewAdminData] = useState({
        email: '',
        name: '',
        password: '',
        confirmPassword: ''
    });
    const [formError, setFormError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchAdmins();
    }, []);

    const fetchAdmins = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await adminInstance.get('/api/admin/auth/admins');
            setAdmins(response.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load admins');
        } finally {
            setLoading(false);
        }
    };

    const handleAddAdmin = async () => {
        // Validate form
        setFormError(null);
        setSubmitting(true);
        
        if (!newAdminData.email || !newAdminData.password || !newAdminData.confirmPassword) {
            setFormError('Email and password fields are required');
            setSubmitting(false);
            return;
        }

        if (newAdminData.password !== newAdminData.confirmPassword) {
            setFormError('Passwords do not match');
            setSubmitting(false);
            return;
        }

        if (newAdminData.password.length < 6) {
            setFormError('Password must be at least 6 characters');
            setSubmitting(false);
            return;
        }

        try {
            await adminInstance.post('/api/admin/auth/admins', {
                email: newAdminData.email,
                name: newAdminData.name || newAdminData.email.split('@')[0],
                password: newAdminData.password
            });
            setSuccess('Admin created successfully');
            resetForm();
            fetchAdmins();
        } catch (err) {
            setFormError(err.response?.data?.message || 'Failed to create admin');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteAdmin = async () => {
        if (!selectedAdmin) return;
        
        try {
            setSubmitting(true);
            await adminInstance.delete(`/api/admin/auth/admins/${selectedAdmin._id}`);
            setSuccess('Admin deleted successfully');
            setOpenDeleteDialog(false);
            fetchAdmins();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to delete admin');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setNewAdminData({
            email: '',
            name: '',
            password: '',
            confirmPassword: ''
        });
        setOpenAddDialog(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setNewAdminData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const confirmDeleteAdmin = (admin) => {
        setSelectedAdmin(admin);
        setOpenDeleteDialog(true);
    };

    if (loading && admins.length === 0) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container>
            <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                    Admin Management
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => setOpenAddDialog(true)}
                >
                    Add Admin
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 3 }}>
                    {success}
                </Alert>
            )}

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Email</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Created</TableCell>
                            <TableCell>Last Login</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {admins.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No admins found
                                </TableCell>
                            </TableRow>
                        ) : (
                            admins.map((admin) => (
                                <TableRow key={admin._id}>
                                    <TableCell>{admin.email}</TableCell>
                                    <TableCell>{admin.name || 'N/A'}</TableCell>
                                    <TableCell>{admin.role || 'Admin'}</TableCell>
                                    <TableCell>
                                        {new Date(admin.createdAt).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        {admin.lastLogin 
                                            ? new Date(admin.lastLogin).toLocaleString() 
                                            : 'Never'}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="Delete Admin">
                                            <IconButton 
                                                color="error"
                                                onClick={() => confirmDeleteAdmin(admin)}
                                                // Prevent deleting your own account
                                                disabled={admin.isCurrent}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add Admin Dialog */}
            <Dialog open={openAddDialog} onClose={() => !submitting && resetForm()}>
                <DialogTitle>Add New Admin</DialogTitle>
                <DialogContent>
                    {formError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {formError}
                        </Alert>
                    )}
                    <TextField
                        autoFocus
                        margin="dense"
                        name="email"
                        label="Email Address"
                        type="email"
                        fullWidth
                        value={newAdminData.email}
                        onChange={handleInputChange}
                        disabled={submitting}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="name"
                        label="Name (Optional)"
                        type="text"
                        fullWidth
                        value={newAdminData.name}
                        onChange={handleInputChange}
                        disabled={submitting}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="password"
                        label="Password"
                        type="password"
                        fullWidth
                        value={newAdminData.password}
                        onChange={handleInputChange}
                        disabled={submitting}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        name="confirmPassword"
                        label="Confirm Password"
                        type="password"
                        fullWidth
                        value={newAdminData.confirmPassword}
                        onChange={handleInputChange}
                        disabled={submitting}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={resetForm} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleAddAdmin} 
                        color="primary" 
                        disabled={submitting}
                        variant="contained"
                    >
                        {submitting ? 'Creating...' : 'Create Admin'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={openDeleteDialog} onClose={() => !submitting && setOpenDeleteDialog(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete admin {selectedAdmin?.email}? This action cannot be undone.
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDeleteDialog(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteAdmin} 
                        color="error" 
                        disabled={submitting}
                        variant="contained"
                    >
                        {submitting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default AdminManagement; 